import * as React from 'react';
import { Observer, observer } from 'mobx-react-lite';
import { Layout, Table, Button, Space, Tag, Popconfirm, Upload, message, Tooltip, Modal, Checkbox } from 'antd';
import { CopyOutlined, InboxOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { DataCrafterTemplatesVisualStore } from '../../stores';
import { DataCrafterTemplate, DataCrafterTemplateStatus } from '../../types';
import LayoutHeader from '../../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import { Utils } from '../../../common/services/Utils';
import { DataCrafterTemplateImportWizard, DuplicateTemplateDialog } from '..';
import { SortOrder } from 'antd/lib/table/interface';
import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { endpoints } from '../../../../config';

type Props = {
    store: DataCrafterTemplatesVisualStore;
};

const DataCrafterTemplatesList: React.FC<Props> = ({ store }) => {
    React.useEffect(() => {
        if (store.currentProject) {
            store.loadTemplates();
            store.setHeaders();
        }
    }, [store, store.currentProject]);

    const getStatusColor = (status: DataCrafterTemplateStatus) => {
        switch (status) {
            case DataCrafterTemplateStatus.Active:
                return 'green';
            case DataCrafterTemplateStatus.Draft:
                return 'gold';
            case DataCrafterTemplateStatus.Archived:
                return 'default';
            default:
                return 'default';
        }
    };

    const handleStatusClick = async (template: DataCrafterTemplate) => {
        const newStatus =
            template.status === DataCrafterTemplateStatus.Draft
                ? DataCrafterTemplateStatus.Active
                : DataCrafterTemplateStatus.Draft;

        await store.changeTemplateStatus(template.id, newStatus);
    };

    const handleDuplicate = (templateId: string) => {
        store.setTemplateToDuplicate(templateId);
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: DataCrafterTemplate, b: DataCrafterTemplate) => a.name.localeCompare(b.name)
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: DataCrafterTemplateStatus, record: DataCrafterTemplate) => {
                return (
                    <Observer>
                        {() => (
                            <Tag
                                color={getStatusColor(status)}
                                style={{
                                    cursor: store.changingStatusTemplateIds.includes(record.id)
                                        ? 'not-allowed'
                                        : 'pointer'
                                }}
                                onClick={
                                    store.changingStatusTemplateIds.includes(record.id)
                                        ? undefined
                                        : () => handleStatusClick(record)
                                }
                            >
                                {status}
                                {store.changingStatusTemplateIds.includes(record.id) && <SyncOutlined spin />}
                            </Tag>
                        )}
                    </Observer>
                );
            },
            filters: Object.values(DataCrafterTemplateStatus)
                .filter(s => s !== DataCrafterTemplateStatus.Archived)
                .map(status => ({
                    text: status,
                    value: status
                })),
            onFilter: (value: string, record: DataCrafterTemplate) => record.status === value
        },
        {
            title: 'Coding Model',
            dataIndex: 'coderModelName',
            key: 'coderModelName',
            render: (coderModelName: string | undefined, record: DataCrafterTemplate) =>
                coderModelName || record.coderModelId || 'Not specified'
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (createdBy: string) => <Observer>{() => <>{store.getUserNameById(createdBy)}</>}</Observer>
        },
        {
            title: 'Last Updated',
            dataIndex: 'updatedTime',
            key: 'lastUpdated',
            defaultSortOrder: 'descend' as SortOrder,
            render: (updatedTime: string | null, record: DataCrafterTemplate) => {
                if (updatedTime) {
                    return Utils.formatDateStringShort(updatedTime);
                }
                return Utils.formatDateStringShort(record.createdTime);
            },
            sorter: (a: DataCrafterTemplate, b: DataCrafterTemplate) => {
                const aTime = a.updatedTime || a.createdTime;
                const bTime = b.updatedTime || b.createdTime;
                return Utils.safeDateStringCompare(aTime, bTime);
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: DataCrafterTemplate) => (
                <div className="row-actions-wrapper">
                    <Space size="middle">
                        <Tooltip title="Edit template">
                            <Button
                                size="small"
                                type="text"
                                style={{ verticalAlign: 'middle' }}
                                icon={<i className="alpha-icon lg edit-icon" style={{ marginTop: -2 }} />}
                                onClick={() => store.navigateToEditTemplate(record.id)}
                            />
                        </Tooltip>
                        <Tooltip title="Duplicate template">
                            <Button
                                size="small"
                                type="text"
                                icon={<CopyOutlined style={{ color: '#9ba0ab' }} />}
                                onClick={() => handleDuplicate(record.id)}
                            />
                        </Tooltip>
                        <Tooltip title="Export template">
                            <Button
                                size="small"
                                type="text"
                                icon={<i className="alpha-icon xs arrow-up" style={{ marginTop: 3 }} />}
                                title="Export template"
                                onClick={() => store.handleExportTemplateClick(record.id)}
                            />
                        </Tooltip>
                        <Tooltip title="Archive template">
                            <Popconfirm
                                title={`Are you sure you want to archive the template "${record.name}"?`}
                                onConfirm={() => store.archiveTemplate(record.id)}
                                okText="Archive"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<InboxOutlined style={{ fontSize: 20 }} />}
                                    danger
                                    title="Archive template"
                                />
                            </Popconfirm>
                        </Tooltip>
                    </Space>
                </div>
            )
        }
    ];

    const isLoading = store.isLoadingTemplates || store.isLoadingUsers;

    const onFileImport = (info: UploadChangeParam) => {
        const status = info.file.status;

        if (status === 'uploading') {
            store.setTemplateImportLoading(true);
            return;
        }

        if (status === 'done') {
            try {
                const formTypeImportResponse = info.file.response;
                store.processTemplateImportData(formTypeImportResponse);
                store.setTemplateImportDialogVisible(true);
                message.success(`${info.file.name} file uploaded successfully.`);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                message.error(`${info.file.name} file import failed. Error: ${errorMessage}`);
                console.error(err);
            } finally {
                store.setTemplateImportLoading(false);
            }
        } else if (status === 'error') {
            store.setTemplateImportLoading(false);
            if (info.file?.response?.status === 409) {
                message.warning(info.file.response.title);
                return;
            }
            const errorMessage = info.file.response?.title ?? info.file.error?.message ?? 'Unknown error';
            message.error(`${info.file.name} file upload failed. Error: ${errorMessage}`);
        }
    };

    const beforeUpload = (file: RcFile) => {
        const extension = file.name.split('.').pop();
        const expectedExtension = 'data_crafter_template';

        if (extension !== expectedExtension) {
            store.setTemplateImportLoading(false);
            message.error(`File must have the ${expectedExtension} extension!`);
            return false;
        }

        return true;
    };

    return (
        <Layout className="data-crafter-templates-list" style={{ height: '100%', background: 'white' }}>
            <LayoutHeader
                subtitle={Utils.getSubtitle(store.currentProject)}
                helpMessage=""
                title="Data Crafter Templates"
                buttons={[
                    <Upload
                        key="upload-data-crafter-template"
                        showUploadList={false}
                        name="file"
                        className="headerButton data-crafter-template-uploader"
                        headers={store.fileImportActionHeaders}
                        action={`${endpoints.manageUrl}projects/${store.currentProject?.id}/data-crafter-templates/import`}
                        beforeUpload={beforeUpload}
                        onChange={onFileImport}
                        disabled={store.templateImportLoading}
                    >
                        <Button
                            data-id="button-Import"
                            key="import-button-container"
                            size="large"
                            loading={store.templateImportLoading}
                            disabled={store.templateImportLoading}
                        >
                            <i className="alpha-icon xs arrow-down" style={{ verticalAlign: 'middle' }} />
                            <span className={'template-import-label'} style={{ marginLeft: 11 }}>
                                Import
                            </span>
                        </Button>
                    </Upload>,
                    <Button
                        key="create-data-crafter-template"
                        data-id="button-create-data-crafter-template"
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => store.navigateToCreateTemplate()}
                    >
                        Create Template
                    </Button>
                ]}
            />
            <Modal
                title="Export Data Crafter Template"
                visible={store.exportOptionsDialogVisible}
                onCancel={() => store.setExportOptionsDialogVisible(false)}
                onOk={() => store.confirmExportTemplate()}
                okText="Export"
                cancelText="Cancel"
                destroyOnClose
            >
                <Space direction="vertical" size="middle">
                    <Checkbox
                        checked={store.exportWithModels}
                        onChange={event => store.setExportWithModels(event.target.checked)}
                    >
                        Include related models
                    </Checkbox>
                    <Checkbox
                        checked={store.exportWithInputPackages}
                        onChange={event => store.setExportWithInputPackages(event.target.checked)}
                    >
                        Include input packages
                    </Checkbox>
                    <Checkbox
                        checked={store.exportWithOutputPackage}
                        onChange={event => store.setExportWithOutputPackage(event.target.checked)}
                    >
                        Include output package
                    </Checkbox>
                </Space>
            </Modal>
            <Layout>
                <Content style={{ maxHeight: 'calc(100vh - 70px)', overflowY: 'auto' }}>
                    <Table
                        data-id="data-crafter-templates-table"
                        dataSource={isLoading ? [] : store.templates}
                        className="alpha-portal-table"
                        columns={columns}
                        loading={isLoading}
                        rowKey="id"
                        pagination={false}
                        sticky
                    />
                </Content>
            </Layout>
            <DataCrafterTemplateImportWizard store={store} />
            <DuplicateTemplateDialog store={store} />
        </Layout>
    );
};

export default observer(DataCrafterTemplatesList);
